import { routerNavigatedAction } from "@ngrx/router-store";
import { ActionType, on } from "@ngrx/store";
import { cloneDeep } from "lodash";

import { DiscardOpportunityDialogActions } from "src/app/job/job-create/discard-job-component/discard-job.actions";
import { NewOpportunityActions } from "src/app/new-opportunity/new-opportunity.actions";

import { ADDABLE_LOCATIONS_V2, JOB_LOCATION_TYPES, LocationType } from "../../../global.constants";
import { formatLocationToDisplay } from "../../../jobs/jobs.util";
import { NewOpportunityCustomerActions } from "../../../new-opportunity/new-opportunity-customer/new-opportunity-customer.actions";
import { NewOpportunityLocationsActions } from "../../../new-opportunity/new-opportunity-locations/new-opportunity-locations.actions";
import { convertGoogleLocationToCreateLocationInput } from "../../../services/google-helper.utils";
import { JobCreateCustomerActions } from "../../job-create/job-create-customer/job-create-customer.actions";
import { JobCreateLocationsActions } from "../../job-create/job-create-locations/job-create-locations-state.actions";
import { JobManuallyAddLocationActions } from "../../job-create/job-manually-add-location/job-manually-add-location.actions";
import { getCustomerInfo, getJobCustomerInfo } from "../../job-helpers";
import { getBuildingType, getElevatorType, getFloor, getLegacyAccessNotes, getLocationAccess, getLocationSize } from "../../job-locations-helpers";
import { JobToolActions } from "../../job-tool.actions";

import { JobLocationsOverviewActions } from "./overview-locations.actions";
import { jobLocationsOverviewInitialState, type LocationEditorState, type OverviewLocationsState } from "./overview-locations.state";

const orderedLocationTypes = JOB_LOCATION_TYPES
  .map((l) => l.key)
  .filter((key) => key !== 'dock')
  .concat('dock');

export const overviewLocationsFeatureReducers = [
  on(
    JobCreateLocationsActions.jobLocationsLoaded,
    (state: OverviewLocationsState, action): OverviewLocationsState => {

      const { job } = action;

      // Populate location inputs based on job
      const locationEditors: Record<LocationType, LocationEditorState> = {};

      for (const location of job.locations) {
        locationEditors[location?.locationType] = {
          selectedLocation: location?.location,
          address: formatLocationToDisplay(location?.location, true),
          unitNumber: location?.location?.addressLineTwo,
          fieldsModified: false,
          locationModified: false,
        };
      }

      for (const locationType of ['start', 'end'] as const) {
        const editor = locationEditors[locationType];

        if (!editor) { continue; }

        editor.buildingType = getBuildingType(job, locationType);
        editor.access = getLocationAccess(job, locationType);
        editor.size = getLocationSize(job, locationType);
        editor.elevatorType = getElevatorType(job, locationType);
        editor.floor = getFloor(job, locationType);

        const accessNotes = getLegacyAccessNotes(job, locationType);

        if (accessNotes) {
          editor.accessNotes = accessNotes;
        }
      }

      let activeLocationType = state.activeLocationType;

      if (
        action.promptedBy.type === JobToolActions.jobLoaded.type ||
        action.promptedBy.type === JobLocationsOverviewActions.deleteJobLocationSuccess.type ||
        action.promptedBy.type === JobToolActions.jobDataReset.type
      ) {
        const jobLocationTypes = job.locations.map((l) => l.locationType);
        activeLocationType = orderedLocationTypes.find((locationType) => jobLocationTypes.includes(locationType));
      }

      return {
        ...state,
        locationEditors: {
          billing: state.locationEditors.billing,
          ...locationEditors,
        },
        activeLocationType,
        serviceArea: undefined,
      };
    },
  ),
  on(
    JobCreateCustomerActions.jobCustomerLoaded,
    (state: OverviewLocationsState, { job }): OverviewLocationsState => {
      const customerInfo = getJobCustomerInfo(job);

      const locationEditors = { ...state.locationEditors };

      if (customerInfo.selectedCustomer?.billingAddress) {
        locationEditors['billing'] = {
          selectedLocation: customerInfo.selectedCustomer.billingAddress,
          address: customerInfo.selectedCustomer.billingAddress.addressLineOne,
          unitNumber: customerInfo.selectedCustomer.billingAddress.addressLineTwo,
          locationModified: false,
          fieldsModified: false,
        };
      } else {
        locationEditors['billing'] = undefined;
      }

      const endAddress = job?.locations?.find(((l) => l.locationType === 'end'))?.location?.addressLineOne;

      const billingAddress = customerInfo?.selectedCustomer?.billingAddress?.addressLineOne;

      return {
        ...state,
        locationEditors,
        useEndLocationAsBillingLocation: !billingAddress || endAddress === billingAddress,
      };
    },
  ),
  on(
    JobCreateCustomerActions.cancelEditCustomerButtonClicked,
    JobToolActions.jobLoaded,
    NewOpportunityActions.unsavedChangesCleared,
    (state: OverviewLocationsState): OverviewLocationsState => {
      const locationEditors = { ...state.locationEditors };

      const jobLocationTypes = [
        'start',
        'end',
        'dock',
        ...ADDABLE_LOCATIONS_V2.map((l) => l.key),
      ];

      for (const locationType of jobLocationTypes) {
        delete locationEditors[locationType];
      }

      locationEditors.billing = undefined;

      return {
        ...state,
        locationEditors,
        serviceArea: undefined,
      };
    },
  ),
  on(
    JobCreateCustomerActions.existingCustomerSelected,
    JobCreateCustomerActions.existingCustomerPreSelected,
    (state: OverviewLocationsState, res): OverviewLocationsState => {
      const customerInfo = getCustomerInfo(res.existingCustomer);

      const locationEditors = { ...state.locationEditors };

      if (customerInfo.selectedCustomer?.billingAddress) {
        locationEditors['billing'] = {
          selectedLocation: customerInfo.selectedCustomer.billingAddress,
          address: customerInfo.selectedCustomer.billingAddress.addressLineOne,
          unitNumber: customerInfo.selectedCustomer.billingAddress.addressLineTwo,
          fieldsModified: false,
          locationModified: false,
        };
      } else {
        delete locationEditors['billing'];
      }

      return {
        ...state,
        locationEditors,
      };
    },
  ),
  on(
    JobCreateCustomerActions.clearCustomerButtonClicked,
    (state: OverviewLocationsState): OverviewLocationsState => {
      return {
        ...state,
        locationEditors: {
          ...state.locationEditors,
          billing: {
            selectedLocation: undefined,
            address: '',
            unitNumber: '',
            locationModified: false,
            fieldsModified: false,
          },
        },
      };
    },
  ),
  on(
    JobCreateCustomerActions.updateCustomerDataSuccess,
    (state: OverviewLocationsState): OverviewLocationsState => {
      return {
        ...state,
        locationEditors: {
          ...state.locationEditors,
          billing: undefined,
        },
      };
    },
  ),
  on(
    JobToolActions.updateJobAreaSuccess,
    (state: OverviewLocationsState, { updatedJob: job }): OverviewLocationsState => {

      const dockLocation = job?.locations?.find((l) => l.locationType === 'dock');

      if (!dockLocation) return state;

      const locationEditors = { ...state.locationEditors };

      if (!('dock' in locationEditors)) {
        locationEditors['dock'] = {
          selectedLocation: dockLocation?.location,
          address: formatLocationToDisplay(dockLocation?.location, true),
          unitNumber: dockLocation?.location?.addressLineTwo,
          fieldsModified: false,
          locationModified: false,
        };
      }

      const activeLocationType = state.activeLocationType === 'dock' ? state.activeLocationType : 'dock';
      const shouldFocusAutocomplete = state.activeLocationType === 'dock' ? state.shouldFocusAutocomplete : true;

      return {
        ...state,
        locationEditors,
        activeLocationType,
        shouldFocusAutocomplete,
      };
    },
  ),
  on(
    JobLocationsOverviewActions.locationSelected,
    (state: OverviewLocationsState, { locationType }): OverviewLocationsState => {
      return {
        ...state,
        activeLocationType: locationType,
        shouldFocusAutocomplete: true,
      };
    },
  ),
  on(
    JobLocationsOverviewActions.addJobLocationSuccess,
    JobLocationsOverviewActions.deleteJobLocationSuccess,
    (state: OverviewLocationsState, { locationType }): OverviewLocationsState => {
      const locationEditors = cloneDeep(state.locationEditors);
      const locationsSaving = cloneDeep(state.locationsSaving);

      delete locationEditors[locationType];
      delete locationsSaving[locationType];

      let serviceArea = cloneDeep(state.serviceArea);

      if (locationType === 'start') {
        serviceArea = undefined;
      }

      return {
        ...state,
        locationEditors,
        locationsSaving,
        serviceArea,
      };
    },
  ),
  on(
    JobLocationsOverviewActions.deleteLocationButtonClicked,
    (state: OverviewLocationsState): OverviewLocationsState => {

      if (!state.activeLocationType) return state;

      const locationsSaving = cloneDeep(state.locationsSaving);

      locationsSaving[state.activeLocationType] = true;

      return {
        ...state,
        locationsSaving,
      };
    },
  ),
  on(
    NewOpportunityActions.startLocationWizardCompleted,
    (state: OverviewLocationsState): OverviewLocationsState => {

      const locationEditors = cloneDeep(state.locationEditors);

      if (!locationEditors.end) {
        locationEditors.end = {
          selectedLocation: undefined,
          address: '',
          unitNumber: '',
          fieldsModified: false,
          locationModified: false,
        }
      }

      return {
        ...state,
        activeLocationType: 'end',
        shouldFocusAutocomplete: true,
        locationEditors,
      };
    },
  ),
  on(
    NewOpportunityActions.createJobSuccess,
    (state: OverviewLocationsState, { promptedBy }): OverviewLocationsState => {

      if (promptedBy.type !== NewOpportunityActions.locationsCompleted.type) {
        return state;
      }

      return {
        ...state,
        activeLocationType: 'start',
        shouldFocusAutocomplete: true,
      };
    },
  ),
  on(
    JobLocationsOverviewActions.addableLocationTypeSelected,
    JobLocationsOverviewActions.addLocationLinkClicked,
    NewOpportunityLocationsActions.exitMinimalViewButtonClicked,
    (state: OverviewLocationsState, action): OverviewLocationsState => {

      const locationType = 'locationType' in action ? action.locationType : 'start';

      return {
        ...state,
        activeLocationType: locationType,
        shouldFocusAutocomplete: true,
        locationEditors: {
          ...state.locationEditors,
          [locationType]: {
            selectedLocation: undefined,
            address: '',
            unitNumber: '',
            fieldsModified: false,
            locationModified: false,
          },
        },
      };
    },
  ),
  on(
    NewOpportunityLocationsActions.googleAutocompleteAddressSelected,
    NewOpportunityLocationsActions.addLocationManuallyButtonClicked,
    (state: OverviewLocationsState, { locationType }): OverviewLocationsState => ({
      ...state,
      activeLocationType: locationType,
    }),
  ),
  on(
    JobLocationsOverviewActions.googleAutocompleteInputChanged,
    JobCreateLocationsActions.googleAutocompleteInputChanged,
    NewOpportunityCustomerActions.googleAutocompleteInputChanged,
    NewOpportunityLocationsActions.googleAutocompleteInputChanged,
    (state: OverviewLocationsState, { address, locationType }): OverviewLocationsState => {
      return {
        ...state,
        locationEditors: {
          ...state.locationEditors,
          [locationType]: {
            ...state.locationEditors?.[locationType],
            address,
            selectedLocation: undefined,
          },
        },
      };
    },
  ),
  on(
    JobLocationsOverviewActions.googleAutocompleteAddressSelected,
    JobCreateLocationsActions.googleAutocompleteAddressSelected,
    NewOpportunityCustomerActions.googleAutocompleteAddressSelected,
    NewOpportunityLocationsActions.googleAutocompleteAddressSelected,
    (state: OverviewLocationsState, action): OverviewLocationsState => {

      const { address, locationType } = action;

      if (!('address_components' in address)) return state;

      const location = {
        ...convertGoogleLocationToCreateLocationInput(address),
        public: false,
      };

      return {
        ...state,
        locationEditors: {
          ...state.locationEditors,
          [locationType]: {
            ...state.locationEditors?.[locationType],
            selectedLocation: location,
            address: location.addressLineOne,
            locationModified: true,
          },
        },
        shouldFocusWizard: action.type === NewOpportunityLocationsActions.googleAutocompleteAddressSelected.type,
      };
    },
  ),
  on(JobLocationsOverviewActions.wizardFocused, (state: OverviewLocationsState) => {
    return {
      ...state,
      shouldFocusWizard: false,
    }
  }),
  on(
    JobLocationsOverviewActions.unitNumberEntered,
    JobCreateLocationsActions.unitNumberEntered,
    NewOpportunityCustomerActions.unitNumberEntered,
    (state: OverviewLocationsState, { locationType, unitNumber }): OverviewLocationsState => {
      return {
        ...state,
        locationEditors: {
          ...state.locationEditors,
          [locationType]: {
            ...state.locationEditors?.[locationType],
            unitNumber,
            locationModified: true,
          },
        },
      };
    },
  ),
  on(
    JobLocationsOverviewActions.buildingTypeSelected,
    JobCreateLocationsActions.buildingTypeSelected,
    (state: OverviewLocationsState, { locationType, buildingType }): OverviewLocationsState => {
      return {
        ...state,
        locationEditors: {
          ...state.locationEditors,
          [locationType]: {
            ...state.locationEditors?.[locationType],
            buildingType,
            size: undefined,
            access: undefined,
            fieldsModified: true,
          },
        }
      };
    },
  ),
  on(
    JobLocationsOverviewActions.sizeSelected,
    JobCreateLocationsActions.sizeSelected,
    (state: OverviewLocationsState, { locationType, size }): OverviewLocationsState => {
      return {
        ...state,
        locationEditors: {
          ...state.locationEditors,
          [locationType]: {
            ...state.locationEditors?.[locationType],
            size,
            fieldsModified: true,
          },
        }
      };
    },
  ),
  on(
    JobLocationsOverviewActions.accessSelected,
    JobCreateLocationsActions.accessSelected,
    (state: OverviewLocationsState, { locationType, access }): OverviewLocationsState => {

      const currentFloor = state.locationEditors[locationType]?.floor;

      return {
        ...state,
        locationEditors: {
          ...state.locationEditors,
          [locationType]: {
            ...state.locationEditors?.[locationType],
            access,
            elevatorType: undefined,
            floor: [ 'none', 'outdoors' ].includes(access) ? 1 : currentFloor > 1 ? currentFloor : 2,
            fieldsModified: true,
          },
        }
      };
    },
  ),
  on(
    JobLocationsOverviewActions.elevatorTypeSelected,
    JobCreateLocationsActions.elevatorTypeSelected,
    (state: OverviewLocationsState, { locationType, elevatorType }): OverviewLocationsState => {
      return {
        ...state,
        locationEditors: {
          ...state.locationEditors,
          [locationType]: {
            ...state.locationEditors?.[locationType],
            elevatorType,
            fieldsModified: true,
          },
        }
      };
    },
  ),
  on(
    JobLocationsOverviewActions.floorInputChanged,
    JobCreateLocationsActions.floorInputChanged,
    (state: OverviewLocationsState, { locationType, floor }): OverviewLocationsState => {
      return {
        ...state,
        locationEditors: {
          ...state.locationEditors,
          [locationType]: {
            ...state.locationEditors?.[locationType],
            floor,
            fieldsModified: true,
          },
        }
      };
    },
  ),
  on(
    JobLocationsOverviewActions.accessNotesTextAreaContentChanged,
    JobCreateLocationsActions.accessNotesTextAreaContentChanged,
    (state: OverviewLocationsState, { locationType, accessNotes }): OverviewLocationsState => {
      return {
        ...state,
        locationEditors: {
          ...state.locationEditors,
          [locationType]: {
            ...state.locationEditors?.[locationType],
            accessNotes,
            fieldsModified: true,
          },
        }
      };
    },
  ),
  on(
    JobCreateLocationsActions.addStopButtonClicked,
    (state: OverviewLocationsState): OverviewLocationsState => {

      const addableLocations = ADDABLE_LOCATIONS_V2.filter((l) => !state.locationEditors?.[l.key]);
      const newLocation = addableLocations[0];

      if (!newLocation) return state;

      return {
        ...state,
        locationEditors: {
          ...state.locationEditors,
          [newLocation.key]: {
            selectedLocation: undefined,
            address: '',
            unitNumber: '',
            fieldsModified: false,
            locationModified: false,
          },
        }
      };
    },
  ),
  on(
    JobCreateLocationsActions.additionalLocationTypeSelected,
    (state: OverviewLocationsState, { previousLocationType, newLocationType }): OverviewLocationsState => {
      const locationEditors = cloneDeep(state.locationEditors);
      const editorState = locationEditors[previousLocationType];

      delete locationEditors[previousLocationType];

      locationEditors[newLocationType] = {
        ...editorState,
      };

      return {
        ...state,
        locationEditors,
      };
    },
  ),
  on(
    JobManuallyAddLocationActions.addLocationButtonClicked,
    (state: OverviewLocationsState, action): OverviewLocationsState => {
      const {
        locationType,
        streetName,
        streetNumber,
        areaCode,
        city,
        country,
        jurisdiction,
      } = action;

      const addressLineOne = `${streetNumber} ${streetName}, ${city}, ${jurisdiction} ${areaCode}, ${country}`;

      const selectedLocation: typeof state.locationEditors.start.selectedLocation = {
        addressLineOne,
        areaCode,
        city,
        country,
        countryJurisdiction: jurisdiction,
        locationType,
        setManually: true,
      };

      return {
        ...state,
        locationEditors: {
          ...state.locationEditors,
          [locationType]: {
            selectedLocation,
            address: selectedLocation.addressLineOne,
            unitNumber: '',
            fieldsModified: false,
            locationModified: true,
          },
        }
      };
    },
  ),
  on(
    JobCreateLocationsActions.removeAdditionalLocationButtonClicked,
    (state: OverviewLocationsState, { locationType }): OverviewLocationsState => {
      const locationEditors = cloneDeep(state.locationEditors);

      delete locationEditors[locationType];

      return {
        ...state,
        locationEditors,
      };
    },
  ),
  on(
    JobCreateLocationsActions.updateLocationsSuccess,
    (state: OverviewLocationsState): OverviewLocationsState => {

      const locationEditors = cloneDeep(state.locationEditors);

      const jobLocationTypes = [
        'start',
        'end',
        'dock',
        ...ADDABLE_LOCATIONS_V2.map((l) => l.key),
      ];

      for (const locationType of jobLocationTypes) {
        delete locationEditors[locationType];
      }

      return {
        ...state,
        locationEditors,
      };
    },
  ),
  on(
    JobCreateLocationsActions.resolveServiceAreaSuccess,
    (state: OverviewLocationsState, { responseData }): OverviewLocationsState => {
      let serviceArea = state.serviceArea;

      const resolvedServiceArea = responseData.matchedArea || responseData.closestAreas[0];

      if (resolvedServiceArea) {
        serviceArea = {
          id: resolvedServiceArea.zoneId,
          name: resolvedServiceArea.zoneName,
          parentId: resolvedServiceArea.zoneParentId,
        };
      }

      return {
        ...state,
        serviceArea,
      };
    },
  ),
  on(
    JobLocationsOverviewActions.serviceAreaSelected,
    (state: OverviewLocationsState, payload): OverviewLocationsState => {

      if (!shouldSetPendingServiceArea(payload)) {
        return state;
      }

      const { serviceArea } = payload;

      return {
        ...state,
        serviceArea: {
          id: serviceArea.id,
          name: serviceArea.name,
          parentId: serviceArea.parent?.id,
        },
      };
    },
  ),
  on(
    NewOpportunityCustomerActions.useEndLocationAsBillingLocationCheckboxClicked,
    (state: OverviewLocationsState, { checked }): OverviewLocationsState => {

      const locationEditors = cloneDeep(state.locationEditors);
      delete locationEditors.billing;

      return {
        ...state,
        locationEditors,
        useEndLocationAsBillingLocation: checked,
      };
    },
  ),
  on(
    JobLocationsOverviewActions.calculateDistancesSuccess,
    (state: OverviewLocationsState, { calculateDistancesOutput }): OverviewLocationsState => {
      return {
        ...state,
        calculateDistancesOutput,
      };
    },
  ),
  on(
    JobLocationsOverviewActions.autocompleteFocused,
    (state: OverviewLocationsState): OverviewLocationsState => {
      return {
        ...state,
        shouldFocusAutocomplete: false,
      };
    },
  ),
  on(
    JobLocationsOverviewActions.detailFieldsTouched,
    (state: OverviewLocationsState): OverviewLocationsState => {
      return {
        ...state,
        shouldTouchDetailFields: false,
      };
    },
  ),
  on(
    JobLocationsOverviewActions.wizardDetailsLoaded,
    (state: OverviewLocationsState, { locationType, buildingType, size, access, elevatorType, floor }): OverviewLocationsState => {
      return {
        ...state,
        locationEditors: {
          ...state.locationEditors,
          [locationType]: {
            ...state.locationEditors?.[locationType],
            buildingType,
            size,
            access,
            elevatorType,
            floor,
            fieldsModified: true,
          },
        },
        activeLocationType: locationType,
        shouldTouchDetailFields: true,
      };
    },
  ),
  on(
    JobLocationsOverviewActions.navigateToInvalidLocationSuccess,
    (state: OverviewLocationsState, { locationType, existingLocation }): OverviewLocationsState => {
      if (!existingLocation && !(locationType in state.locationEditors)) {
        return {
          ...state,
          activeLocationType: locationType,
          shouldFocusAutocomplete: true,
          locationEditors: {
            ...state.locationEditors,
            [locationType]: {
              selectedLocation: undefined,
              address: '',
              unitNumber: '',
              fieldsModified: false,
              locationModified: false,
            },
          },
        };
      }

      return {
        ...state,
        activeLocationType: locationType,
        shouldFocusAutocomplete: true,
      };
    },
  ),
  on(DiscardOpportunityDialogActions.discardUnsavedChangesButtonClicked, (): OverviewLocationsState => jobLocationsOverviewInitialState),
  on(routerNavigatedAction, (state: OverviewLocationsState, { payload: { event: { urlAfterRedirects } } }): OverviewLocationsState => {
    if (urlAfterRedirects.startsWith('/jobs/new')) {
      return jobLocationsOverviewInitialState;
    }
    return state;
  }),
] as const;

export function shouldSetPendingServiceArea({
  promptedBy,
  hasJob,
}: ActionType<typeof JobLocationsOverviewActions.serviceAreaSelected>): boolean {
  return promptedBy.type === JobCreateLocationsActions.setServiceAreaButtonClicked.type
    || (promptedBy.type === JobLocationsOverviewActions.changeAreaButtonClicked.type && !hasJob);
}
